import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
    AlertCircle, RefreshCw, Filter, GitMerge, Inbox, MessageSquare,
    ChevronLeft, ChevronRight, Clock, Loader2, X, ExternalLink, Ban, KeyRound
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import {
    fetchIncomingDuplicates,
    fetchIncomingDuplicateAffiliates,
    mergeIncomingDuplicate,
    dismissIncomingDuplicate
} from "../../services/crmServices/incomingDuplicateService";
import { fetchAvailableUsers } from "../../services/crmServices/userService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";

const STATUS_STYLES = {
    pending: "bg-amber-100 text-amber-700",
    merged: "bg-emerald-100 text-emerald-700",
    dismissed: "bg-gray-100 text-gray-600"
};

export function IncomingDuplicates() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
    const [summary, setSummary] = useState({ pending: 0, merged: 0, dismissed: 0 });
    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [affiliateFilter, setAffiliateFilter] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [fillEmptyFields, setFillEmptyFields] = useState(true);
    const [mergeStatus, setMergeStatus] = useState(""); // "" = keep current
    const [mergeAgent, setMergeAgent] = useState(""); // "" = keep current
    const [statusOptions, setStatusOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [actionBusy, setActionBusy] = useState(null); // record id currently merging/dismissing

    const toggleExpanded = (id) => {
        setExpanded((prev) => (prev === id ? null : id));
        setMergeStatus("");
        setMergeAgent("");
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchIncomingDuplicates({
                page,
                limit: 50,
                status: statusFilter || undefined,
                affiliate: affiliateFilter || undefined
            });
            setRows(res?.data?.rows || []);
            setPagination(res?.data?.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
            setSummary(res?.data?.summary || { pending: 0, merged: 0, dismissed: 0 });
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load incoming duplicates");
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, affiliateFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetchIncomingDuplicateAffiliates()
            .then((res) => setAffiliates(res?.data || []))
            .catch(() => {});
        Promise.all([fetchLeadStatuses(), fetchAvailableUsers()])
            .then(([statusRes, usersRes]) => {
                setStatusOptions((statusRes?.data || []).map((s) => s.label || s.value).filter(Boolean));
                setAgentOptions(
                    (usersRes?.data || [])
                        .filter((u) => u.active !== false)
                        .map((u) => ({
                            label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
                            value: u._id
                        }))
                );
            })
            .catch(() => {});
    }, []);

    // ── Admin gate ──────────────────────────────────────────────────
    if (user?.position?.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-12">
                <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                    <div className="p-3 bg-red-50 rounded-full w-fit mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-sm text-gray-500">Administrator privileges required.</p>
                </div>
            </div>
        );
    }

    const handleMerge = async (row) => {
        setActionBusy(row._id);
        try {
            const res = await mergeIncomingDuplicate(row._id, {
                fillEmptyFields,
                newStatus: mergeStatus || undefined,
                newAgent: mergeAgent || undefined
            });
            if (res?.status === 200) {
                const d = res.data || {};
                const parts = [`${d.commentsAdded ?? 0} comment(s) added`];
                if (d.fieldsFilled?.length > 0) parts.push(`filled: ${d.fieldsFilled.join(", ")}`);
                if (d.statusSet) parts.push(`status → ${d.statusSet}`);
                if (d.agentAssigned) parts.push(`assigned to ${d.agentAssigned}`);
                toast.success(`Merged into original lead — ${parts.join(", ")}`);
                setExpanded(null);
                load();
            } else {
                toast.error(res?.data?.message || "Merge failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Merge failed");
        } finally {
            setActionBusy(null);
        }
    };

    const handleDismiss = async (row) => {
        setActionBusy(row._id);
        try {
            const res = await dismissIncomingDuplicate(row._id);
            if (res?.status === 200) {
                toast.success("Dismissed");
                setExpanded(null);
                load();
            } else {
                toast.error(res?.data?.message || "Dismiss failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Dismiss failed");
        } finally {
            setActionBusy(null);
        }
    };

    const clearFilters = () => {
        setStatusFilter("");
        setAffiliateFilter("");
        setPage(1);
    };

    const hasFilters = statusFilter || affiliateFilter;

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4 max-w-7xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-sm">
                        <GitMerge className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Incoming Duplicates</h1>
                        <p className="text-xs text-gray-500">
                            Submissions from affiliate API keys that matched an existing lead — review and merge their comments
                        </p>
                    </div>
                </div>
                <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                    {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Refresh
                </Button>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-3 gap-3 mb-5">
                <StatCard icon={Inbox} label="Pending review" value={summary.pending} color="bg-amber-50 text-amber-700" />
                <StatCard icon={GitMerge} label="Merged" value={summary.merged} color="bg-emerald-50 text-emerald-700" />
                <StatCard icon={Ban} label="Dismissed" value={summary.dismissed} color="bg-gray-100 text-gray-600" />
            </div>

            {/* Filters */}
            <Card className="mb-4">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-3">
                        <div>
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                className="h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            >
                                <option value="">All</option>
                                <option value="pending">Pending</option>
                                <option value="merged">Merged</option>
                                <option value="dismissed">Dismissed</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Affiliate key</label>
                            <select
                                value={affiliateFilter}
                                onChange={(e) => { setAffiliateFilter(e.target.value); setPage(1); }}
                                className="h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                            >
                                <option value="">All</option>
                                {affiliates.map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>
                        {hasFilters && (
                            <Button size="sm" variant="outline" onClick={clearFilters}>
                                <X className="h-3.5 w-3.5 mr-1" />
                                Clear filters
                            </Button>
                        )}
                        <div className="ml-auto text-xs text-gray-500">
                            <Filter className="inline h-3 w-3 mr-1" />
                            Showing {rows.length} of {pagination.total} records
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0 overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center">
                            <Loader2 className="h-6 w-6 animate-spin text-amber-500 mx-auto mb-2" />
                            <p className="text-xs text-gray-500">Loading incoming duplicates...</p>
                        </div>
                    ) : error ? (
                        <div className="p-12 text-center">
                            <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-12 text-center">
                            <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-700">Nothing here</p>
                            <p className="text-xs text-gray-500 mt-1">
                                When an affiliate submission matches an existing lead, it will appear here for review.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Received</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Affiliate key</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Incoming submission</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Original lead</th>
                                        <th className="text-center px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Comments</th>
                                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rows.map((row) => {
                                        const isExpanded = expanded === row._id;
                                        const inc = row.incoming || {};
                                        const lead = row.matchedLead;
                                        return (
                                            <>
                                                <tr
                                                    key={row._id}
                                                    className="hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => toggleExpanded(row._id)}
                                                >
                                                    <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                                                        <Clock className="inline h-3 w-3 mr-1 text-gray-400" />
                                                        {formatTime(row.createdAt)}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                                                        <KeyRound className="inline h-3 w-3 mr-1 text-gray-400" />
                                                        {row.affiliateName || "-"}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <div className="text-xs font-medium text-gray-900">
                                                            {`${inc.firstName || ""} ${inc.lastName || ""}`.trim() || <span className="text-gray-400 italic">No name</span>}
                                                        </div>
                                                        <div className="text-[10px] text-gray-500">{inc.email}</div>
                                                        {inc.phone && <div className="text-[10px] text-gray-500 font-mono">{inc.phone}</div>}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        {lead ? (
                                                            <>
                                                                <div className="text-xs font-medium text-gray-900">
                                                                    {`${lead.firstName || ""} ${lead.lastName || ""}`.trim() || lead.email}
                                                                </div>
                                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                                    {lead.status && <Badge variant="outline" className="text-[9px] h-4 px-1.5">{lead.status}</Badge>}
                                                                    {lead.agent && (
                                                                        <span className="text-[10px] text-gray-500">
                                                                            {`${lead.agent.firstName || ""} ${lead.agent.lastName || ""}`.trim()}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span className="text-xs text-red-500 italic">Lead deleted</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-center">
                                                        {row.comments?.length > 0 ? (
                                                            <Badge className="bg-blue-100 text-blue-700 text-[10px] h-5">
                                                                <MessageSquare className="h-2.5 w-2.5 mr-1" />
                                                                {row.comments.length}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <Badge className={`${STATUS_STYLES[row.status] || ""} text-[10px] h-5 capitalize`}>
                                                            {row.status}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-amber-50/30">
                                                        <td colSpan="6" className="px-4 py-4">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                                {/* Incoming data */}
                                                                <div className="bg-white rounded-lg border border-gray-100 p-3">
                                                                    <div className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide mb-2">
                                                                        Incoming submission
                                                                    </div>
                                                                    <FieldGrid data={inc} />
                                                                </div>
                                                                {/* Original lead */}
                                                                <div className="bg-white rounded-lg border border-gray-100 p-3">
                                                                    <div className="flex items-center justify-between mb-2">
                                                                        <div className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">
                                                                            Original lead
                                                                        </div>
                                                                        {lead && (
                                                                            <Link
                                                                                to={`/leads/edit/${lead._id}`}
                                                                                className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                Open lead <ExternalLink className="h-2.5 w-2.5" />
                                                                            </Link>
                                                                        )}
                                                                    </div>
                                                                    {lead ? (
                                                                        <FieldGrid data={lead} extra={{ affiliate: lead.affiliate, created: formatTime(lead.createdAt) }} />
                                                                    ) : (
                                                                        <p className="text-xs text-red-500 italic">
                                                                            The original lead was deleted. You can only dismiss this record.
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Incoming comments */}
                                                            {row.comments?.length > 0 && (
                                                                <div className="bg-white rounded-lg border border-gray-100 p-3 mb-4">
                                                                    <div className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide mb-2">
                                                                        Incoming comments ({row.comments.length})
                                                                    </div>
                                                                    <div className="space-y-2">
                                                                        {row.comments.map((c, i) => (
                                                                            <div key={i} className="text-xs text-gray-700 bg-blue-50/50 rounded-md p-2 whitespace-pre-wrap">
                                                                                {c}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Actions / resolution info */}
                                                            {row.status === "pending" ? (
                                                                <div
                                                                    className="bg-white rounded-lg border border-gray-100 p-3 flex flex-wrap items-end gap-3"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div>
                                                                        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                                                                            Set status
                                                                        </label>
                                                                        <select
                                                                            value={mergeStatus}
                                                                            onChange={(e) => setMergeStatus(e.target.value)}
                                                                            className="h-8 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                                                                        >
                                                                            <option value="">Keep current{lead?.status ? ` (${lead.status})` : ""}</option>
                                                                            {statusOptions.map((s) => (
                                                                                <option key={s} value={s}>{s}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                                                                            Assign to agent
                                                                        </label>
                                                                        <select
                                                                            value={mergeAgent}
                                                                            onChange={(e) => setMergeAgent(e.target.value)}
                                                                            className="h-8 px-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 max-w-[180px]"
                                                                        >
                                                                            <option value="">
                                                                                Keep current{lead?.agent ? ` (${`${lead.agent.firstName || ""} ${lead.agent.lastName || ""}`.trim()})` : ""}
                                                                            </option>
                                                                            {agentOptions.map((a) => (
                                                                                <option key={a.value} value={a.value}>{a.label}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer pb-1.5">
                                                                        <Checkbox
                                                                            checked={fillEmptyFields}
                                                                            onCheckedChange={(v) => setFillEmptyFields(!!v)}
                                                                        />
                                                                        Fill empty fields (never overwrites)
                                                                    </label>
                                                                    <div className="ml-auto flex gap-2">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => handleDismiss(row)}
                                                                            disabled={actionBusy === row._id}
                                                                        >
                                                                            <Ban className="h-3.5 w-3.5 mr-1.5" />
                                                                            Dismiss
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                                                            onClick={() => handleMerge(row)}
                                                                            disabled={actionBusy === row._id || !lead}
                                                                        >
                                                                            {actionBusy === row._id
                                                                                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                                                                : <GitMerge className="h-3.5 w-3.5 mr-1.5" />}
                                                                            Merge into original
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-[11px] text-gray-500">
                                                                    {row.status === "merged" ? "Merged" : "Dismissed"}
                                                                    {row.resolvedBy && ` by ${`${row.resolvedBy.firstName || ""} ${row.resolvedBy.lastName || ""}`.trim()}`}
                                                                    {row.resolvedAt && ` on ${formatTime(row.resolvedAt)}`}
                                                                    {row.status === "merged" && row.mergeDetails && (
                                                                        <>
                                                                            {` — ${row.mergeDetails.commentsAdded ?? 0} comment(s) added`}
                                                                            {row.mergeDetails.fieldsFilled?.length > 0 && `, filled: ${row.mergeDetails.fieldsFilled.join(", ")}`}
                                                                            {row.mergeDetails.statusSet && `, status → ${row.mergeDetails.statusSet}`}
                                                                            {row.mergeDetails.agentAssigned && `, assigned to ${row.mergeDetails.agentAssigned}`}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                            <div className="text-xs text-gray-500">
                                Page {pagination.page} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-1">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setPage(Math.max(1, page - 1))}
                                    disabled={page <= 1}
                                >
                                    <ChevronLeft className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                                    disabled={page >= pagination.totalPages}
                                >
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className={`rounded-lg p-4 ${color} flex items-center justify-between`}>
            <div>
                <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
                <div className="text-2xl font-bold tabular-nums">{(value ?? 0).toLocaleString()}</div>
            </div>
            <Icon className="h-7 w-7 opacity-50" />
        </div>
    );
}

const FIELD_LABELS = {
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone",
    secondPhone: "2nd phone",
    country: "Country",
    city: "City",
    address: "Address",
    source: "Source"
};

function FieldGrid({ data, extra = {} }) {
    return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {Object.entries(FIELD_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-start gap-1.5 text-[11px]">
                    <span className="font-semibold text-gray-400 uppercase tracking-wide shrink-0 text-[9px] mt-0.5 w-16">{label}</span>
                    <span className="text-gray-700 break-all">
                        {data?.[key] ? String(data[key]) : <span className="text-gray-300">—</span>}
                    </span>
                </div>
            ))}
            {Object.entries(extra).map(([key, value]) => (
                value ? (
                    <div key={key} className="flex items-start gap-1.5 text-[11px]">
                        <span className="font-semibold text-gray-400 uppercase tracking-wide shrink-0 text-[9px] mt-0.5 w-16 capitalize">{key}</span>
                        <span className="text-gray-700 break-all">{String(value)}</span>
                    </div>
                ) : null
            ))}
        </div>
    );
}

function formatTime(iso) {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
        hour12: false
    });
}
